import { useState, useEffect } from 'react';
import { collection, query, orderBy, getDocs, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import './StorePage.css';

function StorePage() {
  const [listings, setListings] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedListing, setSelectedListing] = useState(null);
  const { user } = useAuth();

  const fetchListings = async () => {
    try {
      const q = query(
        collection(db, 'marketplace'),
        orderBy('createdAt', 'desc')
      );

      const querySnapshot = await getDocs(q);
      const listingsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: new Date(doc.data().createdAt).toLocaleDateString()
      }));

      setListings(listingsData);
    } catch (error) {
      console.error('Error fetching listings:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchListings();
  }, []);

  const handlePreview = (listing) => {
    setSelectedListing(listing);
  };

  const closePreview = () => {
    setSelectedListing(null);
  };

  const handleDelete = async (listingId) => {
    if (!window.confirm('Are you sure you want to remove this listing from the marketplace?')) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'marketplace', listingId));
      // Refresh the listings after deletion
      await fetchListings();
    } catch (error) {
      console.error('Error deleting listing:', error);
      alert('Failed to delete listing. Please try again.');
    }
  };

  const renderPreview = (listing) => {
    if (!listing?.code?.html) return null;

    const htmlContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <style>${listing.code.css || ''}</style>
        </head>
        <body>
          ${listing.code.html}
          ${listing.code.js ? `<script>${listing.code.js}</script>` : ''}
        </body>
      </html>
    `;

    const blob = new Blob([htmlContent], { type: 'text/html' });
    return URL.createObjectURL(blob);
  };

  if (isLoading) {
    return (
      <div className="store-page">
        <div className="loading-spinner">Loading marketplace...</div>
      </div>
    );
  }

  return (
    <div className="store-page">
      <h1 className="page-title">Marketplace</h1>
      
      <div className="listings-grid">
        {listings.map(listing => (
          <div key={listing.id} className="listing-card">
            <div className="listing-preview">
              <iframe
                src={renderPreview(listing)}
                title={listing.title}
                sandbox="allow-scripts"
              />
              <button
                className="preview-button"
                onClick={() => handlePreview(listing)}
              >
                Full Preview
              </button>
            </div>
            <div className="listing-details">
              <h3>{listing.title}</h3>
              <p className="listing-description">{listing.description}</p>
              <div className="listing-meta">
                <span className="price">${listing.price.toFixed(2)}</span>
                <span className="seller">by {listing.sellerName}</span>
              </div>
              <div className="listing-actions">
                <button className="buy-button">
                  Purchase for ${listing.price.toFixed(2)}
                </button>
                {user && listing.userId === user.uid && (
                  <button 
                    className="delete-button"
                    onClick={() => handleDelete(listing.id)}
                  >
                    Remove from Marketplace
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {selectedListing && (
        <div className="preview-modal">
          <div className="preview-content">
            <button className="close-button" onClick={closePreview}>×</button>
            <h2>{selectedListing.title}</h2>
            <div className="preview-frame-container">
              <iframe
                src={renderPreview(selectedListing)}
                title={selectedListing.title}
                sandbox="allow-scripts"
              />
            </div>
            <div className="preview-details">
              <p>{selectedListing.description}</p>
              <div className="preview-meta">
                <span className="price">${selectedListing.price.toFixed(2)}</span>
                <span className="seller">by {selectedListing.sellerName}</span>
              </div>
              <div className="preview-actions">
                <button className="buy-button">
                  Purchase for ${selectedListing.price.toFixed(2)}
                </button>
                {user && selectedListing.userId === user.uid && (
                  <button 
                    className="delete-button"
                    onClick={() => handleDelete(selectedListing.id)}
                  >
                    Remove from Marketplace
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default StorePage;